import clsx from 'clsx';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { Button, IconButton, LogoMark, Pill } from '@/design-system';
import { ns, useT } from '@/i18n';
import type { PaymentMode } from '@/services/domain';
import { useAcceptMarketing, usePlatformConfig, useSession } from '@/services/queries';
import { paths } from '../../paths';
import { PromoDialog } from './PromoDialog';
import {
  APP_STORE_URL,
  appBannerVisible,
  automated,
  consentDue,
  MARKETING_DELAY_MS,
  PAYMENT_POPUP_DELAY_MS,
  paymentPopupDue,
  PLAY_STORE_URL,
  promoStorage,
  storeFor,
} from './promoRules';
import dialog from './PromoDialog.module.css';
import styles from './Promos.module.css';

/**
 * The shell's promotional layers (P11): app banner on phones, and on the home page the online-payment
 * announcement then the marketing-consent question (never both at once). None for automated browsers.
 */
export function Promos({ banner }: { banner: boolean }) {
  const { pathname } = useLocation();
  const home = pathname === '/';
  const [open, setOpen] = useState<'payment' | 'consent' | null>(null);
  const { data: user } = useSession();
  const { data: config } = usePlatformConfig();
  const bot = automated();

  // Announcement: home, after 1.5 s, at most 3 times (counted when shown).
  useEffect(() => {
    if (bot || !home || !config) return;
    return whenNoOtherDialog(PAYMENT_POPUP_DELAY_MS, () => {
      if (!paymentPopupDue(promoStorage.paymentPopupCount())) return;
      promoStorage.countPaymentPopup();
      setOpen((o) => o ?? 'payment');
    });
  }, [bot, home, config]);

  // Consent: home, signed in, not accepted, not asked in this tab, after 4 s (waits for the announcement).
  const askConsent = !bot && home && consentDue(user, promoStorage.consentShown());
  useEffect(() => {
    if (!askConsent || open) return;
    return whenNoOtherDialog(MARKETING_DELAY_MS, () => {
      if (consentDue(user, promoStorage.consentShown())) setOpen('consent');
    });
  }, [askConsent, open, user]);

  return (
    <>
      {banner && !bot && <AppBanner />}
      {open === 'payment' && config && (
        <PaymentAnnouncement mode={config.paymentMode} onClose={() => setOpen(null)} />
      )}
      {open === 'consent' && <MarketingConsent onClose={() => setOpen(null)} />}
    </>
  );
}

/**
 * Runs `show` after `delay`, or later once no other modal dialog is open (the home page's rating prompt,
 * a search sheet…): promotional layers never stack on top of something the visitor is doing.
 */
function whenNoOtherDialog(delay: number, show: () => void): () => void {
  let timer = 0;
  const attempt = () => {
    if (document.querySelector('[role="dialog"][aria-modal="true"]')) timer = window.setTimeout(attempt, 1000);
    else show();
  };
  timer = window.setTimeout(attempt, delay);
  return () => window.clearTimeout(timer);
}

/* ── Online payment announcement (L7) ── */

const serif = (chunks: ReactNode) => <span className="t-serif">{chunks}</span>;

function PaymentAnnouncement({ mode, onClose }: { mode: PaymentMode; onClose: () => void }) {
  const t = useT(ns.shell);
  return (
    <PromoDialog label={t('promo.payment.label')} onClose={onClose}>
      <span className={dialog.mark}>
        <LogoMark size={44} />
      </span>
      <Pill tone="lime" className={dialog.eyebrow}>
        {t('promo.payment.tag')}
      </Pill>
      <h2 className={clsx('t-display', dialog.title)}>{t.rich('promo.payment.title', { serif })}</h2>
      {/* Decision 30: the copy follows the live payment mode (the legacy text described `commission_only` only). */}
      <p className={dialog.text}>{t(`promo.payment.${mode}`)}</p>
      <div className={dialog.actions}>
        <Button block onClick={onClose}>
          {t('promo.payment.ok')}
        </Button>
      </div>
    </PromoDialog>
  );
}

/* ── Marketing consent (S1, API-AUTH-10) ── */

function MarketingConsent({ onClose }: { onClose: () => void }) {
  const t = useT(ns.shell);
  const accept = useAcceptMarketing();
  // Both answers close it for this tab (legacy: accept also on a failed request).
  const done = () => {
    promoStorage.markConsentShown();
    onClose();
  };
  return (
    <PromoDialog label={t('promo.consent.label')} onClose={done}>
      <span className={dialog.mark}>
        <LogoMark size={44} />
      </span>
      <h2 className={clsx('t-display', dialog.title)}>{t.rich('promo.consent.title', { serif })}</h2>
      <p className={dialog.text}>{t('promo.consent.text')}</p>
      <div className={dialog.actions}>
        <Button block loading={accept.isPending} onClick={() => accept.mutate(undefined, { onSettled: done })}>
          {t('promo.consent.accept')}
        </Button>
        <Button block variant="ghost" onClick={done}>
          {t('promo.consent.later')}
        </Button>
      </div>
      <p className={dialog.fine}>
        {t.rich('promo.consent.privacy', {
          link: (chunks) => (
            <Link to={paths.privacy} onClick={done}>
              {chunks}
            </Link>
          ),
        })}
      </p>
    </PromoDialog>
  );
}

/* ── App banner (L9) ── */

function AppBanner() {
  const t = useT(ns.shell);
  const [store] = useState(() => storeFor(navigator.userAgent));
  const [show, setShow] = useState(() =>
    appBannerVisible(navigator.userAgent, promoStorage.bannerDismissedAt(), Date.now()),
  );
  if (!show || !store) return null;
  const dismiss = () => {
    promoStorage.dismissBanner();
    setShow(false);
  };
  return (
    <aside className={styles.banner} aria-label={t('promo.app.label')}>
      <span className={styles.icon}>
        <LogoMark size={40} />
      </span>
      <span className={styles.text}>
        <b>{t('promo.app.title')}</b>
        <span>{t('promo.app.text')}</span>
      </span>
      <Button
        size="sm"
        variant="lime"
        onClick={() => {
          window.location.href = store === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
        }}
      >
        {t('promo.app.open')}
      </Button>
      <IconButton icon="close" label={t('promo.app.close')} size={42} className={styles.close} onClick={dismiss} />
    </aside>
  );
}
